import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '@/shared/services/apiClient';
import { API_ROUTES } from '@/shared/services/apiRoutes';
import { useSmartPolling } from './useSmartPolling';

export interface CallHistoryItem {
  id: string;
  type: 'recording-request' | 'start-now' | 'scheduled';
  room_id: string;
  customer_id?: string;
  customer_name: string;
  email?: string;
  phone_number?: string;
  customer_email?: string;
  customer_phone?: string;
  project?: string;
  start_time?: string;
  end_time?: string | null;
  ended_at?: string | null;
  duration?: number | null;
  duration_seconds?: number | null;
  status?: 'scheduled' | 'ongoing' | 'completed' | 'missed' | 'ended';
  recording_url?: string | null;
  notes?: string | null;
  has_recording: boolean;
  recording_status?: 'not_started' | 'not_recorded' | 'recording' | 'recorded' | 'failed';
  created_at: string;
  updated_at?: string;
}

interface UseCallHistoryOptions {
  /**
   * Number of calls to fetch per page
   */
  limit?: number;

  /**
   * Enable smart polling
   */
  enablePolling?: boolean;

  /**
   * Polling interval in milliseconds
   */
  pollInterval?: number;

  /**
   * Filter calls by specific user ID (for admins viewing team member calls)
   */
  filterByUser?: string;
}

interface UseCallHistoryReturn {
  calls: CallHistoryItem[];
  loading: boolean;
  error: string | null;
  total: number;
  hasMore: boolean;
  refresh: () => Promise<void>;
  loadMore: () => Promise<void>;
  refreshing: boolean;
}

/**
 * Hook to fetch and manage call history
 * Matches mobile app implementation
 */
export function useCallHistory({
  limit = 50,
  enablePolling = true,
  pollInterval = 20000, // 20 seconds
  filterByUser,
}: UseCallHistoryOptions = {}): UseCallHistoryReturn {
  const [calls, setCalls] = useState<CallHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);

  /**
   * Fetch call history from API
   */
  const fetchCalls = useCallback(
    async (currentOffset: number = 0, append: boolean = false) => {
      try {
        if (!append) {
          setLoading(true);
        }
        setError(null);

        console.log('[useCallHistory] Fetching calls', { offset: currentOffset, limit, filterByUser });

        // Build query string
        let queryString = `offset=${currentOffset}&limit=${limit}`;
        if (filterByUser) {
          queryString += `&filterByUser=${filterByUser}`;
        }

        const response = await apiClient.get<{
          ok: boolean;
          calls: CallHistoryItem[];
          total: number;
          hasMore: boolean;
        }>(`${API_ROUTES.CALL_HISTORY}?${queryString}`);

        if (response.ok) {
          if (append) {
            setCalls((prev) => [...prev, ...response.calls]);
          } else {
            setCalls(response.calls);
          }
          setTotal(response.total);
          setHasMore(response.hasMore);
          setOffset(currentOffset);
        } else {
          throw new Error('Failed to fetch call history');
        }
      } catch (err: any) {
        console.error('[useCallHistory] Error fetching calls:', err);
        setError(err.message || 'Failed to load call history');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [limit, filterByUser]
  );

  /**
   * Refresh call history (reset to first page)
   */
  const refresh = useCallback(async () => {
    setRefreshing(true);
    await fetchCalls(0, false);
  }, [fetchCalls]);

  /**
   * Load more calls (pagination)
   */
  const loadMore = useCallback(async () => {
    if (hasMore && !loading) {
      const newOffset = offset + limit;
      await fetchCalls(newOffset, true);
    }
  }, [hasMore, loading, offset, limit, fetchCalls]);

  /**
   * Initial fetch on mount
   */
  useEffect(() => {
    fetchCalls(0, false);
  }, [fetchCalls]);

  /**
   * Smart polling - only refresh if call count changes
   */
  useSmartPolling({
    pollInterval,
    enabled: enablePolling && !loading && !error,
    onPoll: refresh,
    compareKey: () => `${calls.length}-${calls.filter(c => c.has_recording).length}-${calls.filter(c => c.recording_status === 'recording').length}`,
    pollOnMount: false,
  });

  return {
    calls,
    loading,
    error,
    total,
    hasMore,
    refresh,
    loadMore,
    refreshing,
  };
}
